import React from 'react'
import { Loading, Text } from '@workwhile/ui'
import {
  CommandPaletteOption,
  CopyableValue,
  DetailsDescription,
  DetailsTitle,
  getValue,
  PositionEntry,
  SectionTitleWithButton,
} from '../CommandPalette_Utils'
import { InfoTable } from 'components/InfoTable'
import {
  BadgePercent,
  Building,
  Car,
  Check,
  FileText,
  Hash,
  Package,
  Shield,
  User,
} from 'lucide-react'
import { useCompanyPosition } from 'queries/company'

type PositionDetailsProps = {
  position: PositionEntry
  onNavigate: (positionId: number) => void
  onItemInteraction: (item: CommandPaletteOption) => void
}

export function PositionDetails({
  position,
  onNavigate,
  onItemInteraction,
}: PositionDetailsProps) {
  const { data: positionDetail, isLoading } = useCompanyPosition(
    position.id,
    1000 * 60 * 5 // 5 minutes
  )

  const handleSearch = () => {
    onItemInteraction(position)
    onNavigate(position.id)
  }

  return (
    <>
      <DetailsTitle>
        {getValue(position.name)}
        {isLoading ? (
          <span style={{ marginLeft: '8px' }}>
            <Loading type="button" />
          </span>
        ) : null}
      </DetailsTitle>
      <DetailsDescription>
        <div style={{ marginBottom: '16px' }}>
          <SectionTitleWithButton
            title="Position Details"
            buttonText="Go to Position"
            onButtonClick={handleSearch}
            buttonTitle="Navigate to this position"
          />
          <InfoTable>
            <InfoTable.Row
              icon={<Hash size={14} />}
              label="Position ID"
              value={
                <CopyableValue
                  value={position.id}
                  fieldName="Position ID"
                  item={position}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<FileText size={14} />}
              label="Position Name"
              value={position.name}
            />
            <InfoTable.Row
              icon={<Building size={14} />}
              label="Company Name"
              value={position.companyName}
            />
            <InfoTable.Row
              icon={<Car size={14} />}
              label="Needs Car"
              value={getValue(positionDetail?.needsCar)}
            />
            <InfoTable.Row
              icon={<BadgePercent size={14} />}
              label="Needs License"
              value={getValue(positionDetail?.needsLicense)}
            />
            <InfoTable.Row
              icon={<User size={14} />}
              label="Prescreened"
              value={getValue(positionDetail?.prescreened)}
            />
            <InfoTable.Row
              icon={<Package size={14} />}
              label="Bundling"
              value={getValue(positionDetail?.bundling)}
            />
            <InfoTable.Row
              icon={<Shield size={14} />}
              label="Allow Autocushion"
              value={getValue(positionDetail?.allowAutocushion)}
            />
          </InfoTable>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Text
            fontSize={2}
            fontWeight={500}
            color="text"
            style={{ marginBottom: '8px' }}
          >
            About
          </Text>
          <p>{positionDetail?.about}</p>
        </div>

        <div>
          <Text
            fontSize={2}
            fontWeight={500}
            color="text"
            style={{ marginBottom: '8px' }}
          >
            Requirements
          </Text>
          <InfoTable>
            {positionDetail?.requirements2?.map((req, index) => (
              <InfoTable.Row
                key={index}
                icon={<Check size={14} />}
                label={req.name}
                value={req.credential !== 'none' ? 'Hard requirement' : ''}
              />
            ))}
          </InfoTable>
        </div>
      </DetailsDescription>
    </>
  )
}
